<template>
  <div class="instructor-edit-page">
    <Sidebar />
    <div class="main-content">
      <div class="edit-page-header">
        <button @click="goBack" class="back-button">
          <i class="fas fa-arrow-left"></i> Back
        </button>
        <h1 v-if="instructor.first_name">Edit Instructor: {{ instructor.first_name }} {{ instructor.last_name }}</h1>
        <h1 v-else>Edit Instructor</h1>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <span>Loading instructor details...</span>
      </div>

      <div v-else-if="error && !instructor.id" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <span>{{ error }}</span>
        <button @click="fetchInstructor" class="retry-btn">Retry</button>
      </div>

      <div v-else class="edit-form-container">
        <div class="loading-overlay" v-if="updating">
          <div class="spinner"></div>
          <span>Saving changes...</span>
        </div>
        
        <div v-if="error" class="toast-message" :class="{ 'success-toast': error.includes('successfully') }">
          <i class="fas" :class="error.includes('successfully') ? 'fa-check-circle' : 'fa-exclamation-circle'"></i>
          <span>{{ error }}</span>
        </div>

        <form @submit.prevent="saveInstructor" class="edit-form">
          <!-- Basic Information Section -->
          <div class="form-card profile-section">
            <div class="profile-image-section">
              <div v-if="form.img_url" class="profile-image" :style="`background-image: url(${form.img_url})`">
              </div>
              <div v-else class="profile-image">
                <i class="fas fa-user"></i>
              </div>
              <div class="profile-image-upload">
                <ImageUploader
                  title="Profile Image"
                  placeholder="Click to upload instructor profile image"
                  v-model="form.img_url"
                  @upload-success="onImageUploadSuccess"
                  @upload-error="error = $event.message || 'Upload failed'"
                />
              </div>
            </div>
            
            <div class="instructor-status">
              <span class="status-label">Instructor Status</span>
              <div class="toggle-switch">
                <input 
                  type="checkbox" 
                  id="isAvailable" 
                  v-model="form.is_available"
                />
                <label for="isAvailable" class="toggle-label"></label>
                <span :class="form.is_available ? 'status-available' : 'status-unavailable'">
                  {{ form.is_available ? 'Available' : 'Not Available' }}
                </span>
              </div>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-card">
              <h2 class="card-title">Basic Information</h2>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName">First Name</label>
                  <input 
                    id="firstName" 
                    v-model="form.first_name" 
                    type="text" 
                    required
                    placeholder="Enter first name"
                  />
                </div>

                <div class="form-group">
                  <label for="firstNameAr">First Name in Arabic</label>
                  <input 
                    id="firstName" 
                    v-model="form.first_name_ar" 
                    type="text" 
                    required
                    placeholder="Enter first name in Arabic"
                  />
                </div>

                <div class="form-group">
                  <label for="lastName">Last Name</label>
                  <input 
                    id="lastName" 
                    v-model="form.last_name" 
                    type="text" 
                    required
                    placeholder="Enter last name"
                  />
                </div>

                <div class="form-group">
                  <label for="lastNameAr">Last Name in Arabic</label>
                  <input 
                    id="lastNameAr" 
                    v-model="form.last_name_ar" 
                    type="text" 
                    required
                    placeholder="Enter last name in Arabic"
                  />
                </div>
              </div>

              <div class="form-section">
                <h3 class="section-title">Languages & Specialties</h3>
                
                <div class="form-group">
                  <label for="languages">Languages</label>
                  <div class="tag-input-container">
                    <input 
                      id="languages" 
                      v-model="languagesInput" 
                      type="text" 
                      placeholder="Enter languages (comma-separated)"
                    />
                    <div class="tag-preview" v-if="languagesInput">
                      <span 
                        v-for="(lang, index) in languagesInput.split(',').map(item => item.trim()).filter(item => item !== '')" 
                        :key="'lang-'+index" 
                        class="tag"
                      >
                        {{ lang }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label for="specialties">Specialties</label>
                  <div class="tag-input-container">
                    <input 
                      id="specialties" 
                      v-model="specialtiesInput" 
                      type="text" 
                      placeholder="Enter specialties (comma-separated)"
                    />
                    <div class="tag-preview" v-if="specialtiesInput">
                      <span 
                        v-for="(spec, index) in specialtiesInput.split(',').map(item => item.trim()).filter(item => item !== '')" 
                        :key="'spec-'+index" 
                        class="tag"
                      >
                        {{ spec }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-section">
                <h3 class="section-title">Sessions & Pricing</h3>
                
                <div class="form-row">
                  <div class="form-group">
                    <label for="pricePerSession">Price Per Session</label>
                    <div class="input-with-symbol">
                      <span class="currency-symbol">$</span>
                      <input 
                        id="pricePerSession" 
                        v-model="form.price_per_session" 
                        type="number" 
                        min="0"
                        step="any"
                        required
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="sessionDuration">Session Duration</label>
                    <div class="input-with-symbol">
                      <input 
                        id="sessionDuration" 
                        v-model="form.session_duration" 
                        type="number" 
                        min="0"
                        required
                        placeholder="0"
                      />
                      <span class="time-unit">minutes</span>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label for="timezone">Timezone</label>
                  <input 
                    id="timezone" 
                    v-model="form.timezone" 
                    type="text"
                    placeholder="e.g. UTC+3, GMT-5"
                  />
                </div>
              </div>
            </div>

            <div class="form-card">
              <h2 class="card-title">Expertise & Biography</h2>
              
              <div class="form-group">
                <label for="expertise">Expertise (English)</label>
                <QuillRichTextEditor
                  id="expertise"
                  v-model="form.expertise"
                  label=""
                  placeholder="Enter instructor's expertise in English"
                  :minHeight="'150px'"
                  @input="validateExpertise"
                />
                <div v-if="expertiseError" class="error-text">{{ expertiseError }}</div>
              </div>

              <div class="form-group">
                <label for="expertiseAr">Expertise (Arabic)</label>
                <QuillRichTextEditor
                  id="expertiseAr"
                  v-model="form.expertise_ar"
                  label=""
                  placeholder="أدخل مجال خبرة المدرس بالعربية"
                  :minHeight="'150px'"
                  dir="rtl"
                  @input="validateExpertiseAr"
                />
                <div v-if="expertiseArError" class="error-text">{{ expertiseArError }}</div>
              </div>

              <div class="form-group">
                <label for="bio">Bio (English)</label>
                <QuillRichTextEditor
                  id="bio"
                  v-model="form.bio"
                  label=""
                  placeholder="Enter instructor's biography in English"
                  :minHeight="'250px'"
                />
              </div>

              <div class="form-group">
                <label for="bioAr">Bio (Arabic)</label>
                <QuillRichTextEditor
                  id="bioAr"
                  v-model="form.bio_ar"
                  label=""
                  placeholder="أدخل السيرة الذاتية للمدرس بالعربية"
                  :minHeight="'250px'"
                  dir="rtl"
                />
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="goBack" class="cancel-btn">
              <i class="fas fa-times"></i> Cancel
            </button>
            <button type="submit" class="save-btn" :disabled="updating">
              <i class="fas fa-check"></i> Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Sidebar from '@/components/Sidebar.vue';
import QuillRichTextEditor from '@/components/QuillEditor.vue';
import ImageUploader from '@/components/ImageUploader.vue';

export default {
  name: 'InstructorEditPage',
  components: {
    Sidebar,
    QuillRichTextEditor,
    ImageUploader
  },
  props: {
    instructorId: {
      type: String,
      required: false
    }
  },
  
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const instructor = ref({});
    const loading = ref(true);
    const updating = ref(false);
    const error = ref('');
    const activeTab = ref('en');
    const expertiseError = ref('');
    const expertiseArError = ref('');
    const isToastVisible = ref(false);
    
    const form = ref({
      first_name: '',
      first_name_ar: '',
      last_name: '',
      last_name_ar: '',
      bio: '',
      bio_ar: '',
      expertise: '',
      expertise_ar: '',
      price_per_session: '',
      session_duration: 0,
      specialties: [],
      languages: [],
      timezone: '',
      is_available: true,
      img_url: ''
    });

    const languagesInput = ref('');
    const specialtiesInput = ref('');

    const getInstructorId = () => {
      return props.instructorId || route.params.id;
    };

    const fetchInstructor = async () => {
      loading.value = true;
      error.value = '';
      const token = localStorage.getItem('token');
      const id = getInstructorId();
      
      console.log('Fetching instructor with ID:', id);
      
      try {
        const response = await fetch(`https://api.kewaar.com/api/admin/instructors1/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          console.error('Error response:', response.status, response.statusText);
          throw new Error('Failed to fetch instructor details');
        }

        const result = await response.json();
        console.log('Fetched instructor data:', result);
        instructor.value = result.data;
        
        // Populate form with instructor data
        form.value = {
          first_name: instructor.value.first_name || '',
          first_name_ar: instructor.value.first_name_ar || '',
          last_name: instructor.value.last_name || '',
          last_name_ar: instructor.value.last_name_ar || '',
          bio: instructor.value.bio || '',
          bio_ar: instructor.value.bio_ar || '',
          expertise: instructor.value.expertise || '',
          expertise_ar: instructor.value.expertise_ar || '',
          price_per_session: instructor.value.price_per_session || '',
          session_duration: instructor.value.session_duration || 0,
          specialties: instructor.value.specialties || [],
          languages: instructor.value.languages || [],
          timezone: instructor.value.timezone || '',
          is_available: instructor.value.is_available !== false,
          img_url: instructor.value.img_url || ''
        };
        
        // Populate the comma-separated inputs
        languagesInput.value = form.value.languages.join(', ');
        specialtiesInput.value = form.value.specialties.join(', ');
        
      } catch (err) {
        error.value = err.message;
        console.error('Error fetching instructor:', err);
      } finally {
        loading.value = false;
      }
    };

    const validateExpertise = () => {
      if (form.value.expertise.length > 500) {
        expertiseError.value = 'Expertise should not exceed 500 characters';
      } else {
        expertiseError.value = '';
      }
    };

    const validateExpertiseAr = () => {
      if (form.value.expertise_ar.length > 500) {
        expertiseArError.value = 'Expertise should not exceed 500 characters';
      } else {
        expertiseArError.value = '';
      }
    };

    const showToast = (message) => {
      error.value = message;
      isToastVisible.value = true;
      
      if (message.includes('successfully')) {
        setTimeout(() => {
          isToastVisible.value = false;
          setTimeout(() => {
            error.value = '';
          }, 300); // Wait for animation to complete
        }, 3000); // Show for 3 seconds
      }
    };

    const saveInstructor = async () => {
      // Validate expertise fields before saving
      validateExpertise();
      validateExpertiseAr();
      
      if (expertiseError.value || expertiseArError.value) {
        showToast('Please fix the validation errors before saving');
        return;
      }

      updating.value = true;
      error.value = '';
      const id = getInstructorId();
      
      // Parse comma-separated values into arrays
      form.value.languages = languagesInput.value
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== '');
        
      form.value.specialties = specialtiesInput.value
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== '');
      
      try {
        const token = localStorage.getItem('token');
        
        console.log('Updating instructor with data:', form.value);
        
        const response = await fetch(`https://api.kewaar.com/api/admin/instructors1/${id}`, {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(form.value)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update instructor');
        }
        
        const result = await response.json();
        console.log('Instructor updated successfully:', result);
        
        showToast('Changes saved successfully!');
      } catch (err) {
        showToast(err.message);
        console.error('Error updating instructor:', err);
      } finally {
        updating.value = false;
      }
    };

    const goBack = () => {
      router.push('/instructor-list');
    };

    const onImageUploadSuccess = () => {
      showToast('Profile image uploaded successfully!');
    };

    onMounted(fetchInstructor);

    return {
      instructor,
      form,
      loading,
      updating,
      error,
      activeTab,
      languagesInput,
      specialtiesInput,
      expertiseError,
      expertiseArError,
      isToastVisible,
      saveInstructor,
      goBack,
      fetchInstructor,
      validateExpertise,
      validateExpertiseAr,
      onImageUploadSuccess
    };
  }
};
</script>

<style scoped>
.instructor-edit-page {
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: 1200px;
}

.edit-page-header {
  margin-bottom: 2rem;
  display: flex;
  align-items: baseline;
  gap: 1.5rem;
}

.edit-page-header h1 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: #e2e8f0;
  color: #334155;
}

/* Form cards styling */
.form-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
}

.profile-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
}

.profile-image-section {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.profile-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  position: relative;
  border: 3px solid #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.profile-image i {
  font-size: 2.5rem;
  color: #94a3b8;
}

.profile-image-upload {
  flex: 1;
}

.instructor-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.status-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.status-available {
  color: #10b981;
  font-weight: 500;
}

.status-unavailable {
  color: #ef4444;
  font-weight: 500;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #334155;
  margin-top: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #475569;
  margin: 1.5rem 0 1rem;
}

.form-section {
  margin-top: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #f8fafc;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: #fff;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #94a3b8;
}

/* Special input styling */
.input-with-symbol {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 10px;
  color: #64748b;
}

.input-with-symbol input {
  padding-left: 25px;
}

.time-unit {
  position: absolute;
  right: 10px;
  color: #64748b;
  font-size: 0.875rem;
}

/* Tag input and preview */
.tag-input-container {
  position: relative;
}

.tag-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #e0f2fe;
  color: #0369a1;
  border-radius: 16px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Toggle switch styling */
.toggle-switch {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle-switch input[type="checkbox"] {
  display: none;
}

.toggle-label {
  display: inline-block;
  width: 48px;
  height: 24px;
  background-color: #cbd5e1;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toggle-label:after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  top: 2px;
  left: 2px;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

input[type="checkbox"]:checked + .toggle-label {
  background-color: #10b981;
}

input[type="checkbox"]:checked + .toggle-label:after {
  left: 26px;
}

/* Tab styling */
.tab-card {
  overflow: hidden;
}

.tab-navigation {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 1.5rem;
}

.tab-btn {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s ease;
}

.tab-btn i {
  font-size: 0.875rem;
}

.tab-btn.active {
  color: #3b82f6;
}

.tab-btn.active:after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #3b82f6;
}

.tab-btn:hover:not(.active) {
  color: #334155;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-btn,
.save-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background-color: white;
  border: 1px solid #cbd5e1;
  color: #475569;
}

.cancel-btn:hover {
  background-color: #f1f5f9;
  color: #334155;
}

.save-btn {
  background-color: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
}

.save-btn:hover {
  background-color: #2563eb;
}

.save-btn:disabled {
  background-color: #93c5fd;
  border-color: #93c5fd;
  cursor: not-allowed;
}

/* Loading and error states */
.loading-state, 
.error-state {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 3rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-state {
  color: #64748b;
}

.error-state {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
}

.toast-message {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  max-width: 400px;
}

.toast-message.success-toast {
  background-color: #dcfce7;
  color: #166534;
  border-left: 4px solid #22c55e;
}

.toast-message:not(.success-toast) {
  background-color: #fee2e2;
  color: #b91c1c;
  border-left: 4px solid #ef4444;
}

.toast-message i {
  font-size: 1.25rem;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

.toast-message.leaving {
  animation: slideOut 0.3s ease-in;
}

/* Responsive styles */
@media (max-width: 1200px) {
  .main-content {
    margin-left: 0;
    padding: 1.5rem;
  }
  
  .profile-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
  
  .instructor-status {
    align-items: flex-start;
    margin-top: 0.5rem;
  }
}

@media (max-width: 992px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .form-card {
    padding: 1.25rem;
  }
}

@media (max-width: 768px) {
  .edit-page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }

  .profile-image-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .profile-image-upload {
    width: 100%;
  }

  .instructor-status {
    width: 100%;
    align-items: center;
  }
}

@media (max-width: 576px) {
  .main-content {
    padding: 1rem;
  }
  
  .edit-form {
    padding: 1rem;
  }

  .form-card {
    padding: 1rem;
  }

  .card-title {
    font-size: 1.1rem;
  }

  .form-group input,
  .form-group textarea {
    font-size: 0.9375rem;
  }

  .form-actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
    justify-content: center;
  }
}

.error-text {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-textarea {
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  transition: all 0.2s ease;
  background-color: #f8fafc;
  resize: vertical;
  display: block;
  margin-top: 0.5rem;
  font-family: inherit;
}

.form-textarea:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: #fff;
}

.form-textarea::placeholder {
  color: #94a3b8;
}

.tab-pane {
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}
</style> 